"use client";

import { Popup } from "@/app/components/popup";
import { useRouter, useSearchParams } from "next/navigation";
import { Suspense, useCallback, useEffect, useState } from "react";
import { DEFAULT_GOALS } from "../stores/users";
import { localStorage } from "../utils/local-storage";

export const GoalsPopup = (props: { onClose?: () => void }) => {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <GoalsPopupContent {...props} />
    </Suspense>
  );
};

// Goals interface
interface NutritionGoals {
  kcal: number;
  fat: number;
  carbs: number;
  proteins: number;
}

const GoalsPopupContent = ({ onClose }: { onClose?: () => void }) => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const isOpen = searchParams.get("goals") !== null;

  // Initialize goals from localStorage or use defaults
  const [goals, setGoals] = useState<NutritionGoals>(DEFAULT_GOALS);

  // Load saved goals on component mount
  useEffect(() => {
    const savedGoals = localStorage?.getItem("nutrition_goals");
    if (savedGoals) {
      try {
        const parsedGoals = JSON.parse(savedGoals);
        setGoals(parsedGoals);
      } catch (error) {
        console.error("Failed to parse saved goals:", error);
      }
    }
  }, []);

  const handleClose = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.delete("goals");
    router.push("?" + params.toString(), { scroll: false });
    onClose?.();
  }, [onClose, searchParams, router]);

  // Save goals to localStorage
  const saveGoals = useCallback(() => {
    localStorage?.setItem("nutrition_goals", JSON.stringify(goals));
    handleClose();
  }, [goals, handleClose]);

  // Handle input changes
  const handleChange = (key: keyof NutritionGoals, value: string) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 0) {
      setGoals((prev) => ({ ...prev, [key]: numValue }));
    }
  };

  // Reset goals to defaults
  const resetGoals = () => {
    setGoals(DEFAULT_GOALS);
  };

  return (
    <Popup
      isOpen={isOpen}
      onClose={handleClose}
      className="bg-white dark:bg-zinc-900 max-w-md"
      header={<h1 className="text-xl font-bold">Nutrition Goals</h1>}
    >
      <div className="max-w-md mx-auto px-4 flex flex-col gap-4">
        <div className="">
          <p className=" text-gray-600 dark:text-gray-400 mb-4">
            Set your daily nutrition goals here.
          </p>

          <div className="space-y-4">
            {/* Calories input */}
            <div>
              <label
                htmlFor="kcal-goal"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Calories (kcal)
              </label>
              <input
                id="kcal-goal"
                type="number"
                min="0"
                value={goals.kcal}
                onChange={(e) => handleChange("kcal", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            {/* Fat input */}
            <div>
              <label
                htmlFor="fat-goal"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Fat (g)
              </label>
              <input
                id="fat-goal"
                type="number"
                min="0"
                value={goals.fat}
                onChange={(e) => handleChange("fat", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            {/* Carbs input */}
            <div>
              <label
                htmlFor="carbs-goal"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Carbohydrates (g)
              </label>
              <input
                id="carbs-goal"
                type="number"
                min="0"
                value={goals.carbs}
                onChange={(e) => handleChange("carbs", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            {/* Protein input */}
            <div>
              <label
                htmlFor="protein-goal"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Protein (g)
              </label>
              <input
                id="protein-goal"
                type="number"
                min="0"
                value={goals.proteins}
                onChange={(e) => handleChange("proteins", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <button
              onClick={resetGoals}
              className="px-4 py-2 text-sm font-semibold bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-4xl transition-colors"
            >
              Reset to Defaults
            </button>
            <button
              onClick={saveGoals}
              className="px-4 py-2 text-sm font-semibold text-white bg-sky-500 hover:bg-sky-600 rounded-4xl transition-colors"
            >
              Save Goals
            </button>
          </div>
        </div>
      </div>
    </Popup>
  );
};
